import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowRight, CheckCircle2, Globe } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import Brand from "@/components/Brand";

export default function ForgotPassword() {
  const { t, lang, setLang } = useT();
  const [identifier, setIdentifier] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { identifier: identifier.trim().toLowerCase() });
    } catch (e) { /* always show success to avoid enumeration */ }
    setSent(true);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-pattern px-4 py-6" data-testid="forgot-password-page">
      {/* Prominent language toggle — top corner */}
      <button
        onClick={() => setLang(lang === "ar" ? "en" : "ar")}
        className="fixed top-4 end-4 z-50 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur border border-teal-200 shadow-lg rounded-full px-3 py-1.5 text-xs font-bold text-teal-800 hover:bg-teal-50 hover:border-teal-400 transition"
        data-testid="lang-toggle-forgot-top"
        title={t("language")}
        aria-label={t("language")}
      >
        <Globe size={13} /> {lang === "ar" ? "English" : "عربي"}
      </button>
      <div className="w-full max-w-md">
        <Brand variant="hero" className="mb-5 sm:mb-6" />

        <div className="bg-white rounded-2xl card-shadow p-5 sm:p-7">
          {sent ? (
            <div className="text-center" data-testid="forgot-password-sent">
              <div className="bg-emerald-50 text-emerald-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 size={32} />
              </div>
              <h2 className="text-xl font-bold text-emerald-800 mb-2">{t("forgot_sent_title")}</h2>
              <p className="text-gray-600 text-sm mb-5">{t("forgot_sent_msg")}</p>
              <Link to="/login" className="btn btn-primary inline-flex" data-testid="forgot-back-to-login">
                {t("back_to_login")} <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold text-teal-900 mb-1 text-center">{t("forgot_password_title")}</h2>
              <p className="text-sm text-gray-500 mb-5 text-center">{t("forgot_password_subtitle")}</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="inp-label flex items-center gap-2"><Mail size={13} /> {t("forgot_identifier_label")}</label>
                  <input
                    type="text"
                    autoFocus
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder={t("forgot_identifier_placeholder")}
                    className="inp"
                    style={{ direction: "ltr" }}
                    data-testid="forgot-identifier-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !identifier}
                  className="btn btn-primary w-full justify-center py-3"
                  data-testid="forgot-submit-button"
                >
                  {loading ? t("processing") : t("send_reset_link")}
                </button>
                <div className="text-center text-sm text-gray-600">
                  <Link to="/login" className="text-teal-700 font-semibold hover:underline" data-testid="forgot-cancel-link">
                    {t("back_to_login")}
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>

        <div className="flex justify-center mt-5">
          <button onClick={() => setLang(lang === "ar" ? "en" : "ar")} className="btn btn-secondary" data-testid="lang-toggle-forgot" title={t("language")}>
            <Globe size={14} /> {lang === "ar" ? "English" : "عربي"}
          </button>
        </div>
      </div>
    </div>
  );
}
